<template>
    <div>

        <p v-if="selected.length === 0" class="text-muted mb-0">
            Tick the jobs you want to chase or assign a supplied PO to. Jobs inside their cool-down
            have no checkbox because reminders cannot be sent until they become actionable.
        </p>

        <div v-else>

            <ul class="selectedList list-inline mb-2">
                <li class="list-inline-item">
                    <strong>Checked:</strong>
                </li>
                <!-- Coloured from the server's own class, so a badge here cannot disagree
                     with the button on the row it came from: blue for an early reminder,
                     orange for the customer's last chance, red for the invoice. -->
                <li v-for="job in selected" :key="job.id" class="list-inline-item">
                    <span class="badge" :class="badgeClass(job)">
                        {{ job.reference }}
                    </span>
                </li>
                <li class="list-inline-item">
                    <a href="#" @click.prevent="clearSelection()">Clear?</a>
                </li>
            </ul>

            <!-- The breakdown is shown before the button as well as inside the confirmation:
                 the irreversible action has to be visible without opening a dialog to find
                 it. Stated as a caveat only when it is true — the selection is often all one
                 action, and telling an operator their four identical reminders are "different
                 actions" teaches them to stop reading these. -->
            <p v-if="mixed" class="mb-1">
                These jobs are at different stages, so this will:
            </p>

            <ul class="mb-3">
                <li v-for="group in breakdown" :key="group.label" :class="{ 'text-danger': group.forceFill }">
                    <strong>{{ group.count }} {{ group.count === 1 ? 'job' : 'jobs' }}</strong>
                    — {{ group.label }}
                    <template v-if="group.forceFill">
                        (<strong>invoiced immediately with no purchase order, cannot be undone</strong>)
                    </template>
                </li>
            </ul>

            <button
                type="button"
                class="btn btn-sm"
                :class="forceFillCount > 0 ? 'btn-danger' : 'btn-primary'"
                :disabled="working"
                @click="confirmThenProcess()"
            >
                <span v-if="working">
                    <i class="fa fa-spinner fa-spin me-1"></i> Working&hellip;
                </span>
                <span v-else>
                    {{ actionSummary }}
                </span>
            </button>

            <button
                type="button"
                class="btn btn-sm btn-outline-secondary ms-2"
                :disabled="working || referenceWorking"
                @click="confirmThenSetReference(selected, reload)"
            >
                <span v-if="referenceWorking">
                    <i class="fa fa-spinner fa-spin me-1"></i> Working&hellip;
                </span>
                <span v-else>Set PO &amp; Invoice</span>
            </button>

        </div>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { usePoReference } from '@/composables/usePoReference';

const TABLE = '#datatable-po-list';

const selected = ref([]);
const working = ref(false);
const { confirmThenSetReference, working: referenceWorking } = usePoReference();

/**
 * Grouped by the label the server put on the row, not by anything worked out
 * here. The wording of an action — which reminder of how many, or an invoice
 * without a PO — is decided by PoReminderSettings and PoReminderStage, and a
 * second opinion
 * computed in the browser is how a confirmation ends up promising one thing
 * while the endpoint does another.
 */
const breakdown = computed(() => {
    const groups = [];

    selected.value.forEach((job) => {
        const existing = groups.find((group) => group.label === job.label);

        if (existing) {
            existing.count++;

            return;
        }

        groups.push({
            label: job.label,
            count: 1,
            forceFill: job.stage === 'force_fill',
        });
    });

    // The irreversible rows last, so the sentence an operator reads ends on the
    // part that matters rather than burying it between two reminder counts.
    return groups.sort((a, b) => Number(a.forceFill) - Number(b.forceFill));
});

/**
 * Stage 16 took the invoice-without-a-PO rows off this screen, so in normal use
 * this is always zero. The handling stays because the *endpoint* still decides
 * the action from each job's own state at press time: a page left open long
 * enough for a selected job to cross over would have it invoiced rather than
 * chased, and if that ever happens the operator should at least be told what
 * they are confirming. See the endpoint-intent note in the plan — the real fix
 * is server side, not here.
 */
const forceFillCount = computed(() => selected.value.filter((job) => job.stage === 'force_fill').length);

const remindCount = computed(() => selected.value.length - forceFillCount.value);

/**
 * A badge wearing the row button's severity. The payload carries a Bootstrap
 * button class, and badges take the same words behind `bg-` — swapping the
 * prefix keeps one source for "how serious is this job" rather than a second
 * table of colours here that could drift from the buttons beside it.
 */
const badgeClass = (job) => (job.buttonClass ?? 'btn-primary').replace('btn-', 'bg-');

/**
 * Whether the selection really does carry more than one action. Two reminders
 * at the same point in the chase are one action performed twice, and saying
 * otherwise is the sort of inaccuracy that teaches an operator to skip the
 * dialog.
 */
const mixed = computed(() => breakdown.value.length > 1);

/**
 * What the press does, in one phrase, for the button and the dialog title.
 *
 * Named by *stage* rather than by the row labels: a selection of a first and a
 * second reminder is still "send 2 reminders", while the labels differ. Only
 * a selection that spans both stages falls back to the neutral wording, there
 * being no honest single verb for "chase some and invoice the rest".
 */
const actionSummary = computed(() => {
    const total = selected.value.length;

    if (forceFillCount.value === 0) {
        return `Send ${remindCount.value} ${remindCount.value === 1 ? 'Reminder' : 'Reminders'}`;
    }

    if (remindCount.value === 0) {
        return `Invoice ${total} ${total === 1 ? 'Job' : 'Jobs'} Without PO`;
    }

    return `Action ${total} Jobs`;
});

/**
 * The selection is **read from the checkboxes**, never accumulated alongside
 * them.
 *
 * Adding on tick and filtering on untick looks equivalent and is not: select-all
 * fires a change for every box including the ones already ticked, so an
 * accumulating list counted those jobs twice — visible as a repeated reference
 * in the badges, and fatal at submit time, where duplicate ids are rejected
 * outright by the `distinct` rule on PoBulkActionRequest.
 *
 * Reading the DOM cannot drift from what the operator can see, and is
 * idempotent however many events arrive. It is also what the panels on the Jobs
 * page do at submit time, for the same reason.
 *
 * A redraw replaces every row, so this also clears the selection on paging or
 * filtering: the new rows are unticked, and jobs on another page are not in the
 * DOM and could not be submitted.
 */
const syncFromCheckboxes = () => {
    const checked = [];

    window.jQuery(`${TABLE} .bulk-update-checkbox:checked`).each((_, el) => {
        checked.push(JSON.parse(el.value));
    });

    selected.value = checked;
};

/**
 * Delegated from the document rather than bound to the checkboxes or to the
 * DataTables instance. Two reasons: the table replaces every row on every
 * draw, so directly bound handlers last exactly one page; and this component
 * mounts alongside List.vue, so reaching for the instance here would race with
 * the component that creates it.
 */
onMounted(() => {
    window.jQuery(document).on('change.poBulk', `${TABLE} .bulk-update-checkbox`, syncFromCheckboxes);
    window.jQuery(document).on('draw.dt.poBulk', TABLE, syncFromCheckboxes);
});

onUnmounted(() => {
    window.jQuery(document).off('change.poBulk');
    window.jQuery(document).off('draw.dt.poBulk');
});

const clearSelection = () => {
    window.jQuery(`${TABLE} .bulk-update-checkbox:checked`).each((_, el) => {
        el.checked = false;
    });

    window.jQuery('#po-check-all').prop('checked', false);

    syncFromCheckboxes();
};

const confirmThenProcess = () => {
    if (working.value || selected.value.length === 0) {
        return;
    }

    const detail = breakdown.value
        .map((group) => {
            const jobs = `<strong>${group.count} ${group.count === 1 ? 'job' : 'jobs'}</strong>`;

            return group.forceFill
                ? `<li>${jobs} — ${group.label}: <strong>an invoice is raised with no `
                    + 'purchase order on it and emailed immediately, and this cannot be '
                    + 'undone</strong></li>'
                : `<li>${jobs} — ${group.label}</li>`;
        })
        .join('');

    // The caveat appears only when the selection genuinely spans more than one
    // action. Told that four identical reminders are "a different action per
    // job", an operator learns the dialog is boilerplate — and then reads
    // straight past it on the day it says something that matters.
    // Same sentence either way bar the reason, so the list reads as the
    // continuation of it rather than as a fresh thought.
    const lead = mixed.value
        ? '<p class="mb-2">These jobs are at different stages, so this will:</p>'
        : '<p class="mb-2">This will:</p>';

    Swal.fire({
        icon: forceFillCount.value > 0 ? 'warning' : 'question',
        title: `${actionSummary.value}?`,
        html: `${lead}<ul class="text-start mb-0">${detail}</ul>`,
        showCancelButton: true,
        confirmButtonText: forceFillCount.value > 0 && remindCount.value > 0
            ? `Yes — including ${forceFillCount.value} to invoice`
            : `Yes, ${actionSummary.value.charAt(0).toLowerCase()}${actionSummary.value.slice(1)}`,
        confirmButtonColor: forceFillCount.value > 0 ? '#dc3545' : undefined,
        cancelButtonText: 'Cancel',
        width: '650px',
    }).then((result) => {
        if (result.isConfirmed) {
            process();
        }
    });
};

/**
 * The same endpoint as a single press — a per-row press is a selection of one —
 * so eligibility is re-applied server side for every id and a stale selection
 * cannot force an action the service would refuse.
 */
const process = () => {
    working.value = true;

    const data = new FormData();
    data.append('ids', selected.value.map((job) => job.id).join(','));
    // This screen chases; it does not invoice (Stage 17). A selection made
    // before a job crossed over is refused server side rather than silently
    // invoiced under a button that says it is sending reminders.
    data.append('intent', 'remind');

    axios.post(route('api.movements.po.process'), data)
        .then((response) => {
            working.value = false;
            reportOutcome(response.data);
        })
        .catch((error) => {
            working.value = false;
            reportError(error);
        });
};

/**
 * Always a modal, never a toast. The counts are the point of a bulk press —
 * how many were chased against how many were invoiced — and skipped rows carry
 * a reason the operator needs to read rather than watch disappear.
 */
const reportOutcome = (result) => {
    const sent = result.sent ?? 0;
    const forced = result.forced ?? 0;
    const skipped = result.skipped ?? 0;
    const failed = result.failed ?? 0;
    const messages = result.messages ?? [];

    const summary = `<p class="mb-2"><strong>${sent}</strong> chased`
        + `, <strong>${forced}</strong> invoiced without a purchase order`
        + `, <strong>${skipped}</strong> skipped`
        + `, <strong>${failed}</strong> failed.</p>`;

    const detail = messages.length > 0
        ? `<ul class="text-start small mb-0">${messages.map((message) => `<li>${message}</li>`).join('')}</ul>`
        : '';

    Swal.fire({
        icon: failed > 0 ? 'error' : (skipped > 0 ? 'warning' : 'success'),
        title: 'Done',
        html: `${summary}${detail}`,
        width: '700px',
        confirmButtonText: 'Close',
    }).then(() => reload());
};

const reportError = (error) => {
    if (!error.response) {
        return;
    }

    let allErrors = '';

    if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([, messages]) => {
            messages.forEach((message) => {
                allErrors += '<p class="mb-0">' + message + '</p>';
            });
        });
    } else if (error.response?.data?.message) {
        allErrors = error.response.data.message;
    }

    Swal.fire({
        icon: 'error',
        title: 'Could not action those jobs',
        html: allErrors,
        width: '600px',
        confirmButtonText: 'Dismiss',
        confirmButtonColor: '#dc3545',
    });
};

/**
 * The table rather than the page: a reload would drop the stage filter and the
 * actionable-only toggle back to their defaults, hiding the very rows the
 * operator was working through.
 */
const reload = () => {
    clearSelection();

    window.jQuery(TABLE).DataTable().ajax.reload(null, false);
};
</script>
